import { useState } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";
import { useAuth } from "../contexts/AuthContext";
import { apiUrl } from '../utils/apiBase';

//
// detectEnvironment and getApiBaseUrl removed (logic now handled by apiUrl)

// Funzione per caricare le playlist gestite dal server (ora con autenticazione)
export async function loadManagedPlaylists(authenticatedFetch) {
  try {
    const response = await authenticatedFetch(apiUrl('/api/playlists/selected/content'));
    if (!response.ok) {
      throw new Error('Errore nel caricamento delle playlist gestite');
    }
    
    const result = await response.json();
    return result.content || '';
  } catch (error) {
    console.warn('Playlist gestite non disponibili:', error);
    return '';
  }
}

// Funzione per verificare se il VPN proxy server è disponibile
export async function hasProxyServer() {
  try {
    const response = await fetchWithLog(apiUrl('/health'), {
      method: 'GET',
      signal: AbortSignal.timeout(3000)
    });
    if (response.ok) {
      const data = await response.json();
      // Verifica che sia il nostro Node.js proxy o micro-proxy
      return (
        data.status === 'OK' ||
        data.service === 'vpn-node-proxy' ||
        (data.service === 'vpn-micro-proxy' && data.status === 'healthy')
      );
    }
    return false;
  } catch (error) {
    console.warn('VPN Proxy server non disponibile:', error);
    return false;
  }
}

// Funzione per creare URL sicuri per le immagini (usa sempre apiUrl)
export function createSecureImageUrl(originalUrl) {
  if (!originalUrl) return '';
  
  // 🔧 ELECTRON FIX: Evita ricorsione se l'URL è già un proxy completo
  if (originalUrl.includes('/proxy/image') || originalUrl.includes('localhost:')) {
    // Se è già un URL proxy completo, restituiscilo così com'è
    if (originalUrl.includes('/proxy/image') && originalUrl.includes('localhost:')) {
      return originalUrl;
    }
    // Se è un path relativo del proxy, aggiungici l'API base
    if (originalUrl.startsWith('/proxy/image')) {
      return apiUrl(originalUrl);
    }
    // Se contiene localhost ma non è un proxy, potrebbe essere un errore
    console.warn('[ELECTRON-DEBUG] Suspicious URL passed to createSecureImageUrl:', originalUrl);
    return originalUrl;
  }
  
  if (originalUrl.startsWith('https://')) return originalUrl;
  if (originalUrl.startsWith('http://')) {
    return apiUrl(`/proxy/image?url=${encodeURIComponent(originalUrl)}`);
  }
  return originalUrl;
}

// Funzione per creare URL proxy per stream video (usa sempre apiUrl)
export async function createProxyStreamUrl(originalUrl) {
  if (!originalUrl) return originalUrl;
  if (originalUrl.startsWith('https://')) return originalUrl;
  if (originalUrl.startsWith('http://')) {
    return apiUrl(`/proxy/image?url=${encodeURIComponent(originalUrl)}`);
  }
  return originalUrl;
}

// Funzione helper per pre-processare URL stream durante il parsing (usa sempre apiUrl)
export function preprocessStreamUrl(originalUrl) {
  if (!originalUrl || !originalUrl.startsWith('http://')) return originalUrl;
  return apiUrl(`/proxy/image?url=${encodeURIComponent(originalUrl)}`);
}

export function useM3UPlaylist() {
  const [channels, setChannels] = useState([]);
  const [defaultGroup, setDefaultGroup] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const authenticatedFetch = useAuthenticatedFetch();
  let authUser = null;
  try {
    const ctx = useAuth();
    authUser = ctx.user;
  } catch {
    authUser = null;
  }

  // Funzione per caricare le playlist gestite
  async function loadManagedPlaylistsContent() {
    try {
      console.log('🔄 Caricamento playlist gestite con autenticazione...');
      const content = await loadManagedPlaylists(authenticatedFetch);
      
      if (content && content.trim()) {
        console.log('✅ Playlist gestite caricate, parsing...');
        parseM3U(content);
      } else {
        console.log('ℹ️ Nessuna playlist selezionata');
        setChannels([]);
        setDefaultGroup('');
      }
    } catch (error) {
      console.error('❌ Errore nel caricamento playlist gestite:', error);
      setChannels([]);
      setDefaultGroup('');
    }
  }

  function parseM3U(content) {
    if (!content || typeof content !== 'string') {
      console.error('❌ Contenuto M3U non valido');
      setChannels([]);
      setDefaultGroup('');
      setIsLoading(false);
      return;
    }

    console.log('🔄 Inizio parsing M3U, lunghezza contenuto:', content.length);
    setIsLoading(true);
    setLoadingProgress(0);
    
    // Ambiente rilevato non più necessario: logica centralizzata in apiUrl
    
    const lines = content.split('\n');
    let currentChannel = {};
    const parsed = [];
    const totalLines = lines.length;
    let skippedChannels = 0;
    let currentPlaylistName = null;
    let hasErrors = false;
    
    lines.forEach((line, index) => {
      try {
        // Aggiorna il progresso ogni 10 linee per performance
        if (index % 10 === 0) {
          const progress = Math.round((index / totalLines) * 100);
          setLoadingProgress(progress);
        }
        
        // Rileva i marcatori delle playlist
        if (line.startsWith('# === ') && line.endsWith(' ===')) {
          currentPlaylistName = line.replace(/^# === /, '').replace(/ ===$/, '').trim();
          console.log(`📋 Trovata playlist: ${currentPlaylistName}`);
          return;
        }
        
        if (line.startsWith('#EXTINF')) {
          // Parsing più robusto della linea EXTINF
          // Formato: #EXTINF:-1 tvg-id="..." tvg-name="..." tvg-logo="..." group-title="...",Nome Canale
          
          const commaIndex = line.lastIndexOf(',');
          if (commaIndex === -1) {
            console.warn('⚠️ Linea EXTINF senza virgola:', line.substring(0, 100));
            return;
          }
          
          const paramsSection = line.substring(0, commaIndex);
          const channelName = line.substring(commaIndex + 1).trim();
          
          // Estrazione parametri con regex più robuste
          const extractParam = (paramName) => {
            const regex = new RegExp(`${paramName}="([^"]*)"`, 'i');
            const match = paramsSection.match(regex);
            return match ? match[1] : '';
          };
          
          const tvgId = extractParam('tvg-id');
          const tvgName = extractParam('tvg-name');
          const originalLogoUrl = extractParam('tvg-logo');
          const group = extractParam('group-title') || 'Altro';
          
          let tvgLogo = '';
          if (originalLogoUrl) {
            tvgLogo = createSecureImageUrl(originalLogoUrl);
          }
          
          currentChannel = {
            name: channelName,
            tvgId,
            tvgName,
            tvgLogo,
            originalLogoUrl,
            group,
            url: '',
            originalUrl: '',
            playlistName: currentPlaylistName // Aggiungi informazione playlist
          };
          
          // Log solo per i primi canali per debug
          if (parsed.length < 5) {
          }
        } else if (line.trim() && !line.startsWith('#') && currentChannel.name) {
          const originalUrl = line.trim();
          const processedUrl = preprocessStreamUrl(originalUrl);
          currentChannel.originalUrl = originalUrl;
          currentChannel.url = processedUrl;
          
          // Filtra i canali con tvg-name che inizia con "----" (separatori/intestazioni)
          if (currentChannel.tvgName && currentChannel.tvgName.startsWith('---')) {
            skippedChannels++;
          } else if (authUser?.blockAdult && /^XXX/i.test(currentChannel.group)) {
            skippedChannels++;
          } else {

            parsed.push(currentChannel);
          }
          
          currentChannel = {};
        }
      } catch (e) {
        console.warn('⚠️ Errore parsing linea:', line.substring(0, 100), e);
        hasErrors = true;
      }
    });

    if (hasErrors) {
      console.warn('⚠️ Parsing completato con errori');
    }
    
    console.log('✅ Parsing completato:');
    console.log(`   📺 Canali validi: ${parsed.length}`);
    console.log(`   ⏭️ Separatori saltati: ${skippedChannels}`);
    console.log(`   📊 Totale linee processate: ${totalLines}`);
    console.log(`   ❌ Errori riscontrati: ${hasErrors ? 'SI' : 'NO'}`);
    
    if (parsed.length === 0 && !hasErrors) {
      console.warn('⚠️ Nessun canale valido trovato nella playlist');
    }
    
    parsed.sort((a, b) => a.name.localeCompare(b.name));
    
    // Calcola i gruppi con conteggio canali e ordina alfabeticamente
    const groups = [...new Set(parsed.map(c => c.group))].filter(Boolean);
    groups.sort(); // Ordinamento alfabetico
    
    // Seleziona gruppo predefinito: priorità a "Italia 4K - FHD", altrimenti primo alfabetico
    let firstGroup = '';
    if (groups.length > 0) {
      // Cerca esattamente "Italia 4K - FHD"
      const preferredGroup = groups.find(group => group === 'Italia 4K - FHD');
      firstGroup = preferredGroup || groups[0];
      console.log('🎯 Gruppo predefinito selezionato:', firstGroup, preferredGroup ? '(Italia 4K - FHD trovato)' : '(primo disponibile)');
    }
    
    setChannels(parsed);
    setDefaultGroup(firstGroup);
    setLoadingProgress(100);
    
    // Reset loading state dopo un breve delay per mostrare il 100%
    setTimeout(() => {
      setIsLoading(false);
      setLoadingProgress(0);
    }, 500);
    
    console.log('🎯 Canali impostati, primo gruppo:', firstGroup);
  }

  return { 
    channels, 
    parseM3U, 
    defaultGroup, 
    loadingProgress, 
    isLoading,
    loadManagedPlaylistsContent
  };
}
